/**
 * Unit of Measure — Phase 2 shared types & helpers.
 *
 * Listings store a `dimension` (mass/count/volume) plus a quantity_value/unit
 * and price_value/unit. The DB compat trigger keeps the legacy
 * quantity_grams / price_per_gram in sync, so we can write the new fields
 * exclusively from the UI and let Postgres derive the legacy values.
 */

export type UomDimension = 'mass' | 'count' | 'volume';

export const MASS_UNITS = ['g', 'kg', 'lb', 'oz'] as const;
export const COUNT_UNITS = ['unit'] as const;
export const VOLUME_UNITS = ['ml', 'l', 'floz', 'gal'] as const;

export type MassUnit = typeof MASS_UNITS[number];
export type CountUnit = typeof COUNT_UNITS[number];
export type VolumeUnit = typeof VOLUME_UNITS[number];
export type UomUnit = MassUnit | CountUnit | VolumeUnit;

export const UNIT_LABEL: Record<UomUnit, string> = {
  g: 'g',
  kg: 'kg',
  lb: 'lb',
  oz: 'oz',
  unit: 'units',
  ml: 'ml',
  l: 'L',
  floz: 'fl oz',
  gal: 'gal',
};

export const DIMENSION_LABEL: Record<UomDimension, string> = {
  mass: 'Mass (weight)',
  count: 'Count (units)',
  volume: 'Volume (liquid)',
};

export function unitsForDimension(dim: UomDimension): readonly UomUnit[] {
  if (dim === 'mass') return MASS_UNITS;
  if (dim === 'count') return COUNT_UNITS;
  return VOLUME_UNITS;
}

export function defaultUnitForDimension(dim: UomDimension): UomUnit {
  if (dim === 'mass') return 'g';
  if (dim === 'count') return 'unit';
  return 'ml';
}

/** Default dimension based on a product_form slug. Mirrors backfill logic. */
const COUNT_FORM_SLUGS = new Set([
  'pre-roll, trim',
  'pre-roll, flower-trim blend',
  'pre-roll, flower',
  'pre-rolls',
]);

/**
 * Per-form dimension overrides — mirror of FastAPI `FORM_DIMENSION_OVERRIDES`.
 * Checked BEFORE the category/slug lookup so a form can keep its category
 * (e.g. Clone stays in Biomass for grouping/filtering) while its UoM
 * dimension differs (Clone is sold per-unit).
 */
export const FORM_DIMENSION_OVERRIDES: Record<string, UomDimension> = {
  'clone': 'count',
};

export function dimensionForProductForm(form: string | null | undefined): UomDimension {
  const slug = (form || '').toLowerCase();
  if (slug && slug in FORM_DIMENSION_OVERRIDES) return FORM_DIMENSION_OVERRIDES[slug];
  if (slug && COUNT_FORM_SLUGS.has(slug)) return 'count';
  return 'mass';
}

/**
 * Whether a form requires a per-unit weight (grams) before publishing.
 * Count-dimension forms generally need it so search can derive canonical $/g,
 * EXCEPT for forms that are count by override (e.g. Clone) where a gram
 * weight isn't meaningful.
 */
export function requiresUnitWeight(form: string | null | undefined): boolean {
  const slug = (form || '').toLowerCase();
  if (!slug) return false;
  if (slug in FORM_DIMENSION_OVERRIDES) return false;
  return dimensionForProductForm(slug) === 'count';
}

/**
 * Smart unit defaults per locked pricing conventions:
 *  - Pre-rolls (count): $/unit; quantity in units. Requires unit_weight_g.
 *  - Concentrates & Extracts (mass): $/g, qty in g.
 *  - Bulk flower / biomass (mass): US (USD) → lb; ROW → kg.
 *  - Volume: $/ml default.
 */
const CONCENTRATE_OR_EXTRACT = new Set([
  'bubble hash','charas','full melt','hash','ice water hash','kief',
  'rosin (cold cured)','rosin (warm cured)','live rosin','hash rosin',
  'badder/batter','budder','cake icing','crumble','crystals','custard',
  'distillate','diamonds','frosting','feco','pull and snap','sap','sauce',
  'shatter','sugar','taffy','wax','isolate','live resin','concentrate','edibles',
]);

export function smartUnitDefaults(
  form: string | null | undefined,
  currency: string | null | undefined,
): { dimension: UomDimension; quantityUnit: UomUnit; priceUnit: UomUnit } {
  const dim = dimensionForProductForm(form);
  if (dim === 'count') {
    return { dimension: 'count', quantityUnit: 'unit', priceUnit: 'unit' };
  }
  const slug = (form || '').toLowerCase();
  if (CONCENTRATE_OR_EXTRACT.has(slug)) {
    return { dimension: 'mass', quantityUnit: 'g', priceUnit: 'g' };
  }
  // Bulk flower / biomass: US uses lb, rest of world uses kg.
  const isUS = (currency || '').toUpperCase() === 'USD';
  return {
    dimension: 'mass',
    quantityUnit: isUS ? 'lb' : 'kg',
    priceUnit: isUS ? 'lb' : 'kg',
  };
}

/** Convert a mass value in `unit` to grams. Returns null if unit isn't mass. */
export function massToGrams(value: number, unit: UomUnit): number | null {
  switch (unit) {
    case 'g': return value;
    case 'kg': return value * 1000;
    case 'lb': return value * 453.59237;
    case 'oz': return value * 28.349523125;
    default: return null;
  }
}

/** Convert grams into a target mass unit. Returns null if target isn't mass. */
export function gramsToUnit(grams: number, unit: UomUnit): number | null {
  switch (unit) {
    case 'g': return grams;
    case 'kg': return grams / 1000;
    case 'lb': return grams / 453.59237;
    case 'oz': return grams / 28.349523125;
    default: return null;
  }
}

/** Convert a volume value in `unit` to milliliters. Returns null if not volume. */
export function volumeToMl(value: number, unit: UomUnit): number | null {
  switch (unit) {
    case 'ml': return value;
    case 'l': return value * 1000;
    case 'floz': return value * 29.5735295625;
    case 'gal': return value * 3785.411784;
    default: return null;
  }
}

/** Convert milliliters to a target volume unit. Returns null if not volume. */
export function mlToUnit(ml: number, unit: UomUnit): number | null {
  switch (unit) {
    case 'ml': return ml;
    case 'l': return ml / 1000;
    case 'floz': return ml / 29.5735295625;
    case 'gal': return ml / 3785.411784;
    default: return null;
  }
}

/**
 * Tooltip helper — converts a listing's canonical $/g and grams into the
 * viewer's preferred mass unit. Returns null when conversion is not meaningful
 * (no preference, listing isn't mass, or pref already matches the listing's
 * native unit so a tooltip would be redundant).
 */
export function buildUomTooltip(
  l: ListingUomFields | null | undefined,
  targetUnit: MassUnit | VolumeUnit | null | undefined,
  currencySymbol = ''
): { price: string | null; quantity: string | null } | null {
  if (!l || !targetUnit) return null;
  const dim = (l.dimension as UomDimension) || 'mass';
  if (dim !== 'mass' && dim !== 'volume') return null;

  const isMass = dim === 'mass';
  const validUnits = isMass ? (MASS_UNITS as readonly string[]) : (VOLUME_UNITS as readonly string[]);
  // Target must be in the same dimension as the listing — otherwise no useful conversion.
  if (!validUnits.includes(targetUnit)) return null;

  const defaultUnit = isMass ? 'g' : 'ml';
  const nativePriceUnit = (l.price_unit || defaultUnit) as UomUnit;
  const nativeQtyUnit = (l.quantity_unit || defaultUnit) as UomUnit;
  if (nativePriceUnit === targetUnit && nativeQtyUnit === targetUnit) return null;

  // For mass, fall back to canonical $/g + grams. For volume, use price_value/quantity_value.
  const baseQty = isMass
    ? (l.quantity_grams ?? null)
    : (l.quantity_value != null ? volumeToMl(l.quantity_value, nativeQtyUnit) : null);
  // Canonical "per smallest base unit" price (per g for mass, per ml for volume)
  const basePrice = isMass
    ? (l.price_per_gram ?? null)
    : (l.price_value != null
        ? (() => {
            const mlPerNative = volumeToMl(1, nativePriceUnit);
            return mlPerNative && mlPerNative > 0 ? l.price_value / mlPerNative : null;
          })()
        : null);

  let priceStr: string | null = null;
  if (basePrice != null && nativePriceUnit !== targetUnit) {
    const basePerTarget = isMass ? massToGrams(1, targetUnit) : volumeToMl(1, targetUnit);
    if (basePerTarget != null) {
      const converted = basePrice * basePerTarget;
      const decimals = targetUnit === 'g' || targetUnit === 'ml' ? 3 : 2;
      priceStr = `${currencySymbol}${converted.toLocaleString('en-US', {
        minimumFractionDigits: decimals,
        maximumFractionDigits: decimals,
      })}/${UNIT_LABEL[targetUnit]}`;
    }
  }

  let qtyStr: string | null = null;
  if (baseQty != null && nativeQtyUnit !== targetUnit) {
    const converted = isMass ? gramsToUnit(baseQty, targetUnit) : mlToUnit(baseQty, targetUnit);
    if (converted != null) {
      qtyStr = `${converted.toLocaleString('en-US', {
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
      })} ${UNIT_LABEL[targetUnit]}`;
    }
  }

  if (!priceStr && !qtyStr) return null;
  return { price: priceStr, quantity: qtyStr };
}

/**
 * Derive a canonical $/g preview from current form inputs.
 * Mirrors the DB `sync_listing_uom` trigger logic (best-effort, for live UX).
 */
export function derivePricePerGram(args: {
  dimension: UomDimension;
  priceValue: number | null;
  priceUnit: UomUnit;
  unitWeightG?: number | null;
}): number | null {
  const { dimension, priceValue, priceUnit, unitWeightG } = args;
  if (priceValue == null || !isFinite(priceValue) || priceValue <= 0) return null;
  if (dimension === 'mass') {
    const grams = massToGrams(1, priceUnit);
    if (grams == null || grams === 0) return null;
    return priceValue / grams;
  }
  if (dimension === 'count') {
    if (!unitWeightG || unitWeightG <= 0) return null;
    return priceValue / unitWeightG;
  }
  return null;
}

/** Format a quantity for display, respecting dimension and unit. */
export function formatUomQuantity(
  value: number | null | undefined,
  unit: string | null | undefined,
  dimension: UomDimension | null | undefined = 'mass'
): string {
  if (value === null || value === undefined) return '—';
  const u = (unit || defaultUnitForDimension(dimension || 'mass')) as UomUnit;
  const label = UNIT_LABEL[u] ?? u;
  const formatted = value.toLocaleString('en-US', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  });
  return `${formatted} ${label}`;
}

/** Format a per-unit price (e.g. "$1.250 / g"). */
export function formatUomPrice(
  value: number | null | undefined,
  unit: string | null | undefined,
  currency?: string | null
): string {
  if (value === null || value === undefined) return '—';
  const u = (unit || 'g') as UomUnit;
  const label = UNIT_LABEL[u] ?? u;
  const cur = currency ? `${currency} ` : '';
  return `${cur}${value.toFixed(3)} / ${label}`;
}

/**
 * Listing-shaped helpers — read either the new UoM fields or fall back to
 * legacy gram fields. Use these everywhere a listing is rendered so the UI
 * gracefully handles both old (gram-only) and new (dimension-aware) rows.
 */
export interface ListingUomFields {
  dimension?: string | null;
  quantity_value?: number | null;
  quantity_unit?: string | null;
  price_value?: number | null;
  price_unit?: string | null;
  quantity_grams?: number | null;
  price_per_gram?: number | null;
  unit_weight_g?: number | null;
  case_size?: number | null;
  units_per_pack?: number | null;
  packs_per_case?: number | null;
}

function pickQty(l: ListingUomFields): { value: number | null; unit: UomUnit } {
  const v = l.quantity_value ?? l.quantity_grams ?? null;
  const u = (l.quantity_unit || (l.quantity_value != null ? defaultUnitForDimension((l.dimension as UomDimension) || 'mass') : 'g')) as UomUnit;
  return { value: v, unit: u };
}

function pickPrice(l: ListingUomFields): { value: number | null; unit: UomUnit } {
  const v = l.price_value ?? l.price_per_gram ?? null;
  const u = (l.price_unit || (l.price_value != null ? defaultUnitForDimension((l.dimension as UomDimension) || 'mass') : 'g')) as UomUnit;
  return { value: v, unit: u };
}

/** Format a listing's quantity, e.g. "5 lb", "1.50 kg", "100 units". */
export function formatListingQuantity(l: ListingUomFields | null | undefined): string {
  if (!l) return '—';
  const { value, unit } = pickQty(l);
  if (value === null || value === undefined) return '—';
  // Mass + grams: keep legacy "auto kg over 1000 g" rendering for continuity.
  if (unit === 'g') {
    if (value >= 1000) return `${(value / 1000).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} kg`;
    return `${Math.round(value).toLocaleString('en-US')} g`;
  }
  const formatted = value.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 2 });
  return `${formatted} ${UNIT_LABEL[unit] ?? unit}`;
}

/** Format a listing's per-unit price, e.g. "USD 0.130 /g", "USD 850 /lb". */
export function formatListingPrice(l: ListingUomFields | null | undefined, currencySymbol = ''): string {
  if (!l) return '—';
  const { value, unit } = pickPrice(l);
  if (value === null || value === undefined) return '—';
  const decimals = unit === 'g' ? 3 : 2;
  const num = value.toLocaleString('en-US', { minimumFractionDigits: decimals, maximumFractionDigits: decimals });
  // Singular suffix for the per-unit denominator (e.g. "$1.30/unit", not "/units").
  const suffix = unit === 'unit' ? 'unit' : (UNIT_LABEL[unit] ?? unit);
  return `${currencySymbol}${num}/${suffix}`;
}

/** Per-unit suffix only (e.g. "/g", "/lb", "/unit") — used after a price already rendered. */
export function listingPriceSuffix(l: ListingUomFields | null | undefined): string {
  if (!l) return '/g';
  const { unit } = pickPrice(l);
  const suffix = unit === 'unit' ? 'unit' : (UNIT_LABEL[unit] ?? unit);
  return `/${suffix}`;
}

/** Total price = price_value * quantity_value when units match; otherwise fall back to legacy gram math. */
export function calculateListingTotal(l: ListingUomFields | null | undefined): number | null {
  if (!l) return null;
  const q = pickQty(l);
  const p = pickPrice(l);
  if (q.value === null || p.value === null) return null;
  if (q.unit === p.unit) return q.value * p.value;
  // Mismatched units — convert within dimension when possible.
  const massUnits = MASS_UNITS as readonly string[];
  const volUnits = VOLUME_UNITS as readonly string[];
  if (massUnits.includes(q.unit) && massUnits.includes(p.unit)) {
    const qG = massToGrams(q.value, q.unit);
    const pPerG = (() => {
      const gPerPriceUnit = massToGrams(1, p.unit);
      return gPerPriceUnit && gPerPriceUnit > 0 ? p.value / gPerPriceUnit : null;
    })();
    if (qG != null && pPerG != null) return qG * pPerG;
  }
  if (volUnits.includes(q.unit) && volUnits.includes(p.unit)) {
    const qMl = volumeToMl(q.value, q.unit);
    const mlPerPriceUnit = volumeToMl(1, p.unit);
    const pPerMl = mlPerPriceUnit && mlPerPriceUnit > 0 ? p.value / mlPerPriceUnit : null;
    if (qMl != null && pPerMl != null) return qMl * pPerMl;
  }
  // Last-resort fallback to canonical gram math (mass listings only).
  if (l.quantity_grams != null && l.price_per_gram != null) {
    return l.quantity_grams * l.price_per_gram;
  }
  return null;
}

/**
 * Per-unit size string for count-dimension listings.
 * Returns e.g. `"0.5 g each"` when unit_weight_g is set, otherwise null
 * (callers should not render an empty line).
 */
export function formatUnitSize(l: ListingUomFields | null | undefined): string | null {
  if (!l) return null;
  const dim = (l.dimension as UomDimension) || 'mass';
  if (dim !== 'count') return null;
  const w = l.unit_weight_g;
  if (w == null || !isFinite(w) || w <= 0) return null;
  const formatted = w.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 3 });
  return `${formatted} g each`;
}

/**
 * Pack/case description for retail-packaged products.
 *  - `units_per_pack && packs_per_case`  → "5/pack · 50 packs/case (250/case)"
 *  - `case_size` only                    → "250 units/case"
 *  - none                                → null
 */
export function formatPackCase(l: ListingUomFields | null | undefined): string | null {
  if (!l) return null;
  const upp = l.units_per_pack ?? null;
  const ppc = l.packs_per_case ?? null;
  const cs = l.case_size ?? null;
  if (upp && ppc && upp > 0 && ppc > 0) {
    const total = upp * ppc;
    return `${upp}/pack · ${ppc} packs/case (${total.toLocaleString('en-US')}/case)`;
  }
  if (cs && cs > 0) {
    return `${cs.toLocaleString('en-US')} units/case`;
  }
  return null;
}